import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import api, { type DispatchBoardCandidate } from '../lib/api'
import { useAuth } from '../lib/auth-context'
import PremiumHeader from '../components/PremiumHeader'

function money(cents?: number | null) {
  if (typeof cents !== 'number') return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)
}

function routeSummary(c: DispatchBoardCandidate) {
  const origin = [c.pickupCity, c.originState, c.pickupZip].filter(Boolean).join(' ').trim() || c.pickupZip || '—'
  const destination = [c.deliveryCity, c.destinationState, c.deliveryZip].filter(Boolean).join(' ').trim() || c.deliveryZip || '—'
  return `${origin} -> ${destination}`
}

export default function DispatchBoardPage() {
  const { user } = useAuth()
  const isInternal = ['ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN'].includes(String(user?.accountType || '').toUpperCase())

  const [q, setQ] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [laneId, setLaneId] = useState('')
  const [originState, setOriginState] = useState('')
  const [destinationState, setDestinationState] = useState('')
  const [pickupZip, setPickupZip] = useState('')
  const [deliveryZip, setDeliveryZip] = useState('')
  const [status, setStatus] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)

  const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>([])
  const [builderCustomerId, setBuilderCustomerId] = useState('')
  const [builderLaneId, setBuilderLaneId] = useState('')
  const [builderCarrierPay, setBuilderCarrierPay] = useState('')
  const [builderCustomerPrice, setBuilderCustomerPrice] = useState('')
  const [builderAssignCarrierId, setBuilderAssignCarrierId] = useState('')
  const [builderAssignDriverId, setBuilderAssignDriverId] = useState('')
  const [builderNotes, setBuilderNotes] = useState('')
  const [builderError, setBuilderError] = useState<string | null>(null)
  const [rowCarrierSelections, setRowCarrierSelections] = useState<Record<string, string>>({})
  const [rowDriverSelections, setRowDriverSelections] = useState<Record<string, string>>({})
  const [rowNotes, setRowNotes] = useState<Record<string, string>>({})

  const boardQuery = useQuery({
    queryKey: ['dispatch-board', q, customerId, laneId, originState, destinationState, pickupZip, deliveryZip, status, refreshKey],
    queryFn: async () => {
      const response = await api.dispatchBoard.get({
        q: q || undefined,
        customerId: customerId || undefined,
        laneId: laneId || undefined,
        originState: originState || undefined,
        destinationState: destinationState || undefined,
        pickupZip: pickupZip || undefined,
        deliveryZip: deliveryZip || undefined,
        status: status || undefined,
      })
      return response.data
    },
  })

  const candidates = boardQuery.data?.candidates ?? []
  const selectedCandidates = useMemo(() => {
    const selected = new Set(selectedCandidateIds)
    return candidates.filter((candidate) => selected.has(candidate.id))
  }, [candidates, selectedCandidateIds])

  const selectedUnitMoveIds = useMemo(
    () => selectedCandidates.map((candidate) => candidate.unitMoveId).filter(Boolean) as string[],
    [selectedCandidates]
  )
  const selectedVinInventoryItemIds = useMemo(
    () => selectedCandidates.map((candidate) => candidate.vinInventoryItemId).filter(Boolean) as string[],
    [selectedCandidates]
  )

  const previewMutation = useMutation({
    mutationFn: async () => {
      const response = await api.dispatchBoard.previewLoad({
        selectedUnitMoveIds,
        selectedVinInventoryItemIds,
        customerId: builderCustomerId || undefined,
        laneId: builderLaneId || undefined,
        carrierPayCents: builderCarrierPay ? Math.round(Number(builderCarrierPay) * 100) : undefined,
        customerPriceCents: builderCustomerPrice ? Math.round(Number(builderCustomerPrice) * 100) : undefined,
      })
      return response.data
    },
    onError: (err: any) => {
      setBuilderError(err?.response?.data?.error ?? 'Failed to preview load')
    },
  })

  const createMutation = useMutation({
    mutationFn: async (postToBoard: boolean) => {
      const response = await api.dispatchBoard.createLoad({
        selectedUnitMoveIds,
        selectedVinInventoryItemIds,
        customerId: builderCustomerId || undefined,
        laneId: builderLaneId || undefined,
        carrierPayCents: builderCarrierPay ? Math.round(Number(builderCarrierPay) * 100) : undefined,
        customerPriceCents: builderCustomerPrice ? Math.round(Number(builderCustomerPrice) * 100) : undefined,
        dispatchNotes: builderNotes || undefined,
        postToBoard,
        assignCarrierId: builderAssignCarrierId || undefined,
        assignDriverId: builderAssignDriverId || undefined,
      })
      return response.data
    },
    onSuccess: (data) => {
      window.location.href = `/loads/${data.load.id}/trailer`
    },
    onError: (err: any) => {
      setBuilderError(err?.response?.data?.error ?? 'Failed to create load')
    },
  })

  const postMutation = useMutation({
    mutationFn: async (loadId: string) => api.loads.post(loadId, { notes: rowNotes[loadId] || undefined }),
    onSuccess: () => {
      setRefreshKey((v) => v + 1)
    },
  })

  const assignCarrierMutation = useMutation({
    mutationFn: async (payload: { loadId: string; carrierId: string }) =>
      api.loads.assignCarrier(payload.loadId, {
        carrierId: payload.carrierId,
        notes: rowNotes[payload.loadId] || undefined,
      }),
    onSuccess: () => {
      setRefreshKey((v) => v + 1)
    },
    onError: (err: any) => {
      setBuilderError(err?.response?.data?.error ?? 'Failed to assign carrier')
    },
  })

  const assignDriverMutation = useMutation({
    mutationFn: async (payload: { loadId: string; driverUserId: string }) =>
      api.loads.assignDriver(payload.loadId, {
        driverUserId: payload.driverUserId,
        carrierId: rowCarrierSelections[payload.loadId] || undefined,
        notes: rowNotes[payload.loadId] || undefined,
      }),
    onSuccess: () => {
      setRefreshKey((v) => v + 1)
    },
    onError: (err: any) => {
      setBuilderError(err?.response?.data?.error ?? 'Failed to assign driver')
    },
  })

  const allSelected = candidates.length > 0 && selectedCandidateIds.length === candidates.length
  const warnings = useMemo(() => {
    const customerIds = new Set(selectedCandidates.map((c) => c.customerId).filter(Boolean))
    const laneIds = new Set(selectedCandidates.map((c) => c.laneId).filter(Boolean))
    const derivedWarnings: string[] = []
    if (customerIds.size > 1) derivedWarnings.push('MIXED_CUSTOMERS')
    if (laneIds.size > 1) derivedWarnings.push('MIXED_LANES')
    if (selectedCandidates.some((c) => !c.customerId)) derivedWarnings.push('MISSING_CUSTOMER')
    if (selectedCandidates.some((c) => !c.pickupZip || !c.deliveryZip)) derivedWarnings.push('MISSING_PICKUP_OR_DELIVERY')
    if (selectedCandidates.some((c) => c.decodeStatus === 'INVALID')) derivedWarnings.push('INVALID_VIN')
    return derivedWarnings
  }, [selectedCandidates])

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-4">
      <PremiumHeader
        title="Dispatch Board"
        subtitle="Turn ready vehicles into load plans, assignments, and live work."
        secondaryAction={
          <button onClick={() => setRefreshKey((v) => v + 1)} className="border rounded px-3 py-2 text-sm">Refresh</button>
        }
      />

      {builderError && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{builderError}</div>}

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-2">
        <input className="border rounded px-3 py-2 text-sm md:col-span-2" placeholder="Search VIN / last8 / last6 / order / lane / customer" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="border rounded px-3 py-2 text-sm" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
          <option value="">Customer</option>
          {(boardQuery.data?.customers ?? []).map((customer) => (
            <option key={customer.id} value={customer.id}>{customer.name}</option>
          ))}
        </select>
        <select className="border rounded px-3 py-2 text-sm" value={laneId} onChange={(e) => setLaneId(e.target.value)}>
          <option value="">Lane</option>
          {(boardQuery.data?.lanes ?? []).map((lane) => (
            <option key={lane.id} value={lane.id}>{lane.laneNumber}</option>
          ))}
        </select>
        <input className="border rounded px-3 py-2 text-sm" placeholder="Origin state" value={originState} onChange={(e) => setOriginState(e.target.value.toUpperCase())} />
        <input className="border rounded px-3 py-2 text-sm" placeholder="Destination state" value={destinationState} onChange={(e) => setDestinationState(e.target.value.toUpperCase())} />
        <input className="border rounded px-3 py-2 text-sm" placeholder="Pickup ZIP" value={pickupZip} onChange={(e) => setPickupZip(e.target.value)} />
        <input className="border rounded px-3 py-2 text-sm" placeholder="Delivery ZIP" value={deliveryZip} onChange={(e) => setDeliveryZip(e.target.value)} />
        <select className="border rounded px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Status</option>
          <option value="PENDING">PENDING</option>
          <option value="PLANNED">PLANNED</option>
          <option value="AVAILABLE">AVAILABLE</option>
          <option value="ORDERED">ORDERED</option>
          <option value="DECODED">DECODED</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border rounded-xl overflow-hidden">
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Candidate Units</h2>
              <p className="text-xs text-gray-500">Select VIN inventory and unit moves for load planning.</p>
            </div>
            <div className="text-xs text-gray-500">{boardQuery.data?.totals.totalCandidates ?? 0} candidates</div>
          </div>

          {boardQuery.isLoading && <div className="px-3 py-4 text-sm text-gray-500">Loading candidates...</div>}
          {!boardQuery.isLoading && !candidates.length && <div className="px-3 py-4 text-sm text-gray-500">No candidates match current filters.</div>}

          {!!candidates.length && (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b text-left">
                <tr>
                  <th className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => setSelectedCandidateIds(allSelected ? [] : candidates.map((c) => c.id))}
                    />
                  </th>
                  <th className="px-3 py-2">VIN</th>
                  <th className="px-3 py-2">Vehicle</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Lane</th>
                  <th className="px-3 py-2">Route</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate) => (
                  <tr key={candidate.id} className="border-b last:border-b-0">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedCandidateIds.includes(candidate.id)}
                        onChange={() =>
                          setSelectedCandidateIds((prev) =>
                            prev.includes(candidate.id) ? prev.filter((id) => id !== candidate.id) : [...prev, candidate.id]
                          )
                        }
                      />
                    </td>
                    <td className="px-3 py-2 font-mono">{candidate.vin}</td>
                    <td className="px-3 py-2">{[candidate.year, candidate.make, candidate.model].filter(Boolean).join(' ') || '—'}</td>
                    <td className="px-3 py-2">{candidate.customerName || '—'}</td>
                    <td className="px-3 py-2">{candidate.laneNumber || '—'}</td>
                    <td className="px-3 py-2">{routeSummary(candidate)}</td>
                    <td className="px-3 py-2">{candidate.sourceType}</td>
                    <td className="px-3 py-2">{candidate.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white border rounded-xl p-3 space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Load Builder</h2>
            <p className="text-xs text-gray-500">Compose one load from selected candidates.</p>
          </div>

          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="rounded border bg-gray-50 px-2 py-1 text-xs text-gray-600">Selected units: {selectedCandidates.length}</div>
            <select className="border rounded px-2 py-1" value={builderCustomerId} onChange={(e) => setBuilderCustomerId(e.target.value)}>
              <option value="">Customer (optional if inferred)</option>
              {(boardQuery.data?.customers ?? []).map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
            <select className="border rounded px-2 py-1" value={builderLaneId} onChange={(e) => setBuilderLaneId(e.target.value)}>
              <option value="">Lane (optional)</option>
              {(boardQuery.data?.lanes ?? []).map((lane) => (
                <option key={lane.id} value={lane.id}>{lane.laneNumber}</option>
              ))}
            </select>
            <input className="border rounded px-2 py-1" placeholder="Carrier Pay ($)" value={builderCarrierPay} onChange={(e) => setBuilderCarrierPay(e.target.value)} />
            {isInternal && (
              <input className="border rounded px-2 py-1" placeholder="Customer Price ($)" value={builderCustomerPrice} onChange={(e) => setBuilderCustomerPrice(e.target.value)} />
            )}
            <select className="border rounded px-2 py-1" value={builderAssignCarrierId} onChange={(e) => setBuilderAssignCarrierId(e.target.value)}>
              <option value="">Assign Carrier (optional)</option>
              {(boardQuery.data?.carriers ?? []).map((carrier: any) => (
                <option key={carrier.id} value={carrier.id}>{carrier.legal_name || carrier.dba_name || carrier.id}</option>
              ))}
            </select>
            <select className="border rounded px-2 py-1" value={builderAssignDriverId} onChange={(e) => setBuilderAssignDriverId(e.target.value)}>
              <option value="">Assign Driver (optional)</option>
              {(boardQuery.data?.drivers ?? []).map((driver: any) => (
                <option key={driver.aux_user_id || driver.id} value={driver.aux_user_id || driver.id}>{driver.legal_name || `${driver.first_name || ''} ${driver.last_name || ''}`.trim() || driver.id}</option>
              ))}
            </select>
            <textarea className="border rounded px-2 py-1" placeholder="Dispatch notes" value={builderNotes} onChange={(e) => setBuilderNotes(e.target.value)} rows={3} />
          </div>

          {!!warnings.length && (
            <div className="rounded border border-amber-300 bg-amber-50 px-2 py-2 text-xs text-amber-900">
              {warnings.join(', ')}
            </div>
          )}

          {previewMutation.data && (
            <div className="rounded border bg-gray-50 px-2 py-2 text-xs text-gray-700 space-y-1">
              <div>Vehicles: {previewMutation.data.vehicleCount}</div>
              <div>Distance: {previewMutation.data.estDistanceMiles ?? '—'} mi</div>
              <div>Suggested carrier pay: {money(previewMutation.data.suggestedCarrierPayCents)}</div>
              {isInternal && <div>Suggested customer price: {money(previewMutation.data.suggestedCustomerPriceCents)}</div>}
              {isInternal && <div>Suggested margin: {money(previewMutation.data.suggestedMarginCents)}</div>}
              {!!previewMutation.data.warnings.length && <div>Warnings: {previewMutation.data.warnings.join(', ')}</div>}
            </div>
          )}

          <div className="grid grid-cols-1 gap-2">
            <button
              disabled={!selectedCandidates.length || previewMutation.isPending}
              onClick={() => {
                setBuilderError(null)
                previewMutation.mutate()
              }}
              className="border rounded px-3 py-2 text-sm"
            >
              Preview Load
            </button>
            <button
              disabled={!selectedCandidates.length || createMutation.isPending}
              onClick={() => {
                setBuilderError(null)
                createMutation.mutate(false)
              }}
              className="border rounded px-3 py-2 text-sm"
            >
              Create Draft Load
            </button>
            <button
              disabled={!selectedCandidates.length || createMutation.isPending}
              onClick={() => {
                setBuilderError(null)
                createMutation.mutate(true)
              }}
              className="bg-blue-700 text-white rounded px-3 py-2 text-sm"
            >
              Create + Post Load
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-3 py-2 border-b">
          <h2 className="text-sm font-semibold text-gray-900">Planned / Recent Loads</h2>
        </div>
        {!boardQuery.data?.recentLoads?.length && <div className="px-3 py-4 text-sm text-gray-500">No recent loads.</div>}
        {!!boardQuery.data?.recentLoads?.length && (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-left">
              <tr>
                <th className="px-3 py-2">Load</th>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2">Vehicles</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Carrier / Driver</th>
                <th className="px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {(boardQuery.data?.recentLoads ?? []).map((load) => (
                <tr key={load.id} className="border-b last:border-b-0">
                  <td className="px-3 py-2">{load.loadNumber || load.id}</td>
                  <td className="px-3 py-2">{load.origin}{' -> '}{load.destination}</td>
                  <td className="px-3 py-2">{(load as any).vehicleCount ?? load.vinCount ?? 0}</td>
                  <td className="px-3 py-2">{load.status}</td>
                  <td className="px-3 py-2">{(load as any).assignedCarrierName || '—'} / {(load as any).assignedDriverName || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-2">
                      <Link to={`/loads/${load.id}`} className="text-blue-700 hover:underline">Open load</Link>
                      <Link to={`/loads/${load.id}/trailer`} className="text-indigo-700 hover:underline">Trailer Plan</Link>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => postMutation.mutate(load.id)}
                          disabled={postMutation.isPending || String(load.status).toUpperCase() === 'POSTED'}
                          className="rounded border border-blue-300 px-2 py-1 text-xs text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                        >
                          Post
                        </button>
                        <select
                          value={rowCarrierSelections[load.id] ?? ''}
                          onChange={(e) => setRowCarrierSelections((prev) => ({ ...prev, [load.id]: e.target.value }))}
                          className="rounded border border-gray-300 px-2 py-1 text-xs"
                        >
                          <option value="">Carrier</option>
                          {(boardQuery.data?.carriers ?? []).map((carrier: any) => (
                            <option key={carrier.id} value={carrier.id}>{carrier.legal_name || carrier.dba_name || carrier.id}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => assignCarrierMutation.mutate({ loadId: load.id, carrierId: rowCarrierSelections[load.id] || '' })}
                          disabled={assignCarrierMutation.isPending || !rowCarrierSelections[load.id]}
                          className="rounded border border-purple-300 px-2 py-1 text-xs text-purple-700 hover:bg-purple-50 disabled:opacity-50"
                        >
                          Assign Carrier
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={rowDriverSelections[load.id] ?? ''}
                          onChange={(e) => setRowDriverSelections((prev) => ({ ...prev, [load.id]: e.target.value }))}
                          className="rounded border border-gray-300 px-2 py-1 text-xs"
                        >
                          <option value="">Driver</option>
                          {(boardQuery.data?.drivers ?? []).map((driver: any) => (
                            <option key={driver.aux_user_id || driver.id} value={driver.aux_user_id || driver.id}>
                              {driver.legal_name || `${driver.first_name || ''} ${driver.last_name || ''}`.trim() || driver.id}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => assignDriverMutation.mutate({ loadId: load.id, driverUserId: rowDriverSelections[load.id] || '' })}
                          disabled={assignDriverMutation.isPending || !rowDriverSelections[load.id]}
                          className="rounded border border-violet-300 px-2 py-1 text-xs text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                        >
                          Assign Driver
                        </button>
                      </div>
                      <input
                        value={rowNotes[load.id] ?? ''}
                        onChange={(e) => setRowNotes((prev) => ({ ...prev, [load.id]: e.target.value }))}
                        placeholder="Note"
                        className="rounded border border-gray-300 px-2 py-1 text-xs"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
